import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useInfiniteBoardActivity } from '../../hooks/useInfiniteBoardActivity';
import { useBoardActivitySummary } from '../../hooks/useBoardActivitySummary';
import { groupActivityByDay, getActivityDayLabel } from '../../utils/group-activity-by-day';
import { BoardActivityFilters } from './BoardActivityFilters';
import { BoardActivityFeedItem } from './BoardActivityFeedItem';
import { BoardActivitySummaryPanel } from './BoardActivitySummaryPanel';
import type { BoardActivityFilterValue } from '../../utils/board-activity-category';

interface BoardActivityViewProps {
  projectId: string;
  boardId: string;
}

export function BoardActivityView({ projectId, boardId }: BoardActivityViewProps) {
  const { t } = useTranslation('tasks');
  const [category, setCategory] = useState<BoardActivityFilterValue | null>(null);

  const { activities, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading, isError } =
    useInfiniteBoardActivity(projectId, boardId, category ?? undefined);
  const { summary, isLoading: isSummaryLoading } = useBoardActivitySummary(projectId, boardId);

  const groups = groupActivityByDay(activities, (item) => item.createdAt);

  return (
    <div
      className="flex h-full flex-col gap-6 overflow-auto p-6"
      data-testid="tasks-board-activity-view"
    >
      <BoardActivityFilters active={category} onChange={setCategory} />

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="min-w-0 flex-1" data-testid="tasks-board-activity-feed">
          {isLoading && (
            <div className="flex flex-col gap-2.5">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-[70px] w-full rounded-lg" />
              ))}
            </div>
          )}

          {!isLoading && isError && (
            <div
              className="rounded-lg border border-border bg-background px-4 py-6 text-center text-sm text-muted-foreground"
              data-testid="tasks-board-activity-error"
            >
              {t('activity.feed.error')}
            </div>
          )}

          {!isLoading && !isError && activities.length === 0 && (
            <div
              className="rounded-lg border border-border bg-background px-4 py-6 text-center text-sm text-muted-foreground"
              data-testid="tasks-board-activity-empty"
            >
              {t('activity.feed.empty')}
            </div>
          )}

          {!isLoading && !isError && activities.length > 0 && (
            <div className="flex flex-col gap-5">
              {groups.map(({ key, items }) => (
                <div key={key} className="flex flex-col gap-2.5">
                  <p className="text-[11.5px] font-medium uppercase text-muted-foreground">
                    {getActivityDayLabel(t, key)}
                  </p>
                  {items.map((activity) => (
                    <BoardActivityFeedItem
                      key={activity.id ?? activity.createdAt}
                      activity={activity}
                    />
                  ))}
                </div>
              ))}

              {hasNextPage && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs text-muted-foreground"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  data-testid="tasks-board-activity-load-more"
                >
                  {isFetchingNextPage ? t('loading') : t('activity.feed.loadMore')}
                </Button>
              )}
            </div>
          )}
        </div>

        <BoardActivitySummaryPanel summary={summary} isLoading={isSummaryLoading} />
      </div>
    </div>
  );
}
